import { ITEM_PICKUP_TUNING } from "../data/tuning.js";

export class ItemPickup {
  constructor(scene, x, y, itemKey, healAmount = 5) {
    this.scene = scene;
    this.baseX = x;
    this.baseY = y;
    this.centerBaseY = y - ITEM_PICKUP_TUNING.bubbleSizePx * 0.5;
    this.itemKey = itemKey;
    this.healAmount = healAmount;
    this.itemSprite = null;
    this.bubbleSprite = null;
    this.elapsedMs = 0;
    this.bobPhase = Math.random() * Math.PI * 2;
    this.markedForRemoval = false;
    this.collected = false;
    this.respawnAtMs = 0;
  }

  create() {
    this.itemSprite = this.scene.add.image(this.baseX, this.baseY, this.itemKey)
      .setOrigin(0.5, 0.5)
      .setDisplaySize(ITEM_PICKUP_TUNING.itemSizePx, ITEM_PICKUP_TUNING.itemSizePx)
      .setDepth(5);

    this.bubbleSprite = this.scene.add.image(this.baseX, this.baseY, "collectible_bubble")
      .setOrigin(0.5, 0.5)
      .setDisplaySize(ITEM_PICKUP_TUNING.bubbleSizePx, ITEM_PICKUP_TUNING.bubbleSizePx)
      .setAlpha(ITEM_PICKUP_TUNING.bubbleAlpha)
      .setDepth(5 + ITEM_PICKUP_TUNING.bubbleDepthOffset);
  }

  update(dt) {
    if (this.markedForRemoval) return;
    if (this.collected) {
      if (this.scene.time.now >= this.respawnAtMs) {
        this.collected = false;
        this.create();
      }
      return;
    }
    this.elapsedMs += dt;
    const t = this.elapsedMs / 1000;
    const angle = (t * Math.PI * 2 * ITEM_PICKUP_TUNING.bobSpeedHz) + this.bobPhase;
    const bubbleBob = Math.sin(angle) * ITEM_PICKUP_TUNING.bobAmplitudePx;
    const itemBob = Math.sin(angle - ITEM_PICKUP_TUNING.itemLagRadians) * ITEM_PICKUP_TUNING.itemBobAmplitudePx;

    this.bubbleSprite.x = this.baseX;
    this.itemSprite.x = this.baseX;
    this.bubbleSprite.y = this.centerBaseY + bubbleBob;
    this.itemSprite.y = this.centerBaseY + itemBob;
  }

  getBounds() {
    if (!this.itemSprite) {
      return {
        x: this.baseX,
        y: this.baseY,
        w: 0,
        h: 0,
      };
    }
    const pad = ITEM_PICKUP_TUNING.pickupPadding;
    const w = this.itemSprite.displayWidth;
    const h = this.itemSprite.displayHeight;
    return {
      x: this.itemSprite.x - w / 2 - pad,
      y: this.itemSprite.y - h / 2 - pad,
      w: w + pad * 2,
      h: h + pad * 2,
    };
  }

  isCollectible() {
    return !this.markedForRemoval && !this.collected && !!this.itemSprite && !!this.bubbleSprite;
  }

  collect(byEntity) {
    if (!this.isCollectible()) return;
    if (typeof byEntity?.hp === "number" && typeof byEntity?.maxHp === "number" && byEntity.hp >= byEntity.maxHp) {
      return;
    }
    const prevHp = typeof byEntity?.hp === "number" ? byEntity.hp : null;
    // TODO(phase-backlog): replace auto-heal with inventory add/equip loop.
    if (byEntity?.heal && !byEntity.dead && !byEntity.dying) {
      byEntity.heal(this.healAmount);
    }
    const healed = prevHp != null && typeof byEntity?.hp === "number"
      ? Math.max(0, byEntity.hp - prevHp)
      : this.healAmount;
    if (healed > 0) this.spawnHealText(healed, byEntity);
    this.collected = true;
    this.respawnAtMs = this.scene.time.now + ITEM_PICKUP_TUNING.respawnDelayMs;

    const popDuration = ITEM_PICKUP_TUNING.popDurationMs;
    const popScale = ITEM_PICKUP_TUNING.popScale;
    const rise = ITEM_PICKUP_TUNING.popRisePx;
    const tweenOut = target => {
      const toScaleX = target.scaleX * popScale;
      const toScaleY = target.scaleY * popScale;
      return this.scene.tweens.add({
        targets: target,
        y: target.y - rise,
        alpha: 0,
        scaleX: toScaleX,
        scaleY: toScaleY,
        duration: popDuration,
        ease: "Back.easeOut",
      });
    };

    tweenOut(this.itemSprite);
    this.scene.tweens.add({
      targets: this.bubbleSprite,
      y: this.bubbleSprite.y - rise,
      alpha: 0,
      scaleX: this.bubbleSprite.scaleX * popScale,
      scaleY: this.bubbleSprite.scaleY * popScale,
      duration: popDuration,
      ease: "Back.easeOut",
      onComplete: () => {
        this.destroy();
      },
    });
  }

  spawnHealText(amount, byEntity = null) {
    const hasEntityAnchor = Number.isFinite(byEntity?.x) && Number.isFinite(byEntity?.y) && Number.isFinite(byEntity?.w);
    const textX = hasEntityAnchor ? (byEntity.x + byEntity.w / 2) : (this.itemSprite?.x ?? this.baseX);
    const textY = hasEntityAnchor ? (byEntity.y - 28) : ((this.itemSprite?.y ?? this.centerBaseY) - 32);
    const txt = this.scene.add.text(textX, textY, `+${amount}`, {
      fontFamily: "monospace",
      fontSize: "14px",
      fontStyle: "bold",
      color: "#98ff98",
      stroke: "#000000",
      strokeThickness: 3,
    }).setOrigin(0.5, 1).setDepth(5000 + textY);

    this.scene.tweens.add({
      targets: txt,
      y: txt.y - 36,
      alpha: 0,
      duration: 1400,
      ease: "Power1",
      onComplete: () => txt.destroy(),
    });
  }

  destroy() {
    if (this.itemSprite) {
      this.itemSprite.destroy();
      this.itemSprite = null;
    }
    if (this.bubbleSprite) {
      this.bubbleSprite.destroy();
      this.bubbleSprite = null;
    }
  }
}
